import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "@/lib/site-data";

function AnimatedNumber({ value }) {
  // Animate only purely numeric portions
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const match = /^([\d.]+)(.*)$/.exec(value);
    if (!match || !inView) { setDisplay(value); return; }
    const final = parseFloat(match[1]);
    const suffix = match[2] || "";
    const decimals = (match[1].split(".")[1] || "").length;
    let start = 0;
    const duration = 1400;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const current = start + (final - start) * eased;
      setDisplay(current.toFixed(decimals) + suffix);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return <span ref={ref}>{display}</span>;
}

export default function Stats() {
  return (
    <section data-testid="stats-section" className="relative bg-white py-20 lg:py-28 border-y border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">By the numbers</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mt-3">
            Standards that define <span className="gold-gradient-text font-medium">trust</span>.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`stat-${i}`}
              className="border-l border-[#E5E5E5] pl-5 lg:pl-6"
            >
              <div className="font-heading text-4xl lg:text-5xl font-light text-[#0A0A0A] gold-gradient-text">
                <AnimatedNumber value={s.value} />
              </div>
              <div className="mt-2 text-xs lg:text-sm text-[#4B5563] uppercase tracking-[0.14em]">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
